import { STATIONS_URL } from '../config/stationsConfig';
import type {
  PollutantChange,
  PollutantReading,
  Station,
} from '../types/station';
import { fetchWithCache } from './cache';

type ApiPollutantReading = {
  value: number;
  setAt: string;
  hasValue: boolean;
  change: number;
};

type ApiStation = {
  id: number;
  name: string;
  state: string;
  region: string;
  latitude: number;
  longitude: number;
  aqi: number;
  pollutants: string;
  confirmed: boolean;
  isActive: boolean;
  pollutantValues: Record<string, ApiPollutantReading>;
};

type ApiStationsResponse = Record<string, ApiStation>;

// The API reports `1` for an increased value, `-1` for a decreased value and
// `0` when the value is unchanged (or there is no previous reading).
const toPollutantChange = (change: number): PollutantChange => {
  if (change > 0) {
    return 'increased';
  }

  if (change < 0) {
    return 'decreased';
  }

  return 'unchanged';
};

const getReading = (
  station: ApiStation,
  pollutant: string,
): PollutantReading | undefined => {
  const reading = station.pollutantValues?.[pollutant];

  if (!reading?.hasValue) {
    return undefined;
  }

  return {
    value: reading.value,
    change: toPollutantChange(reading.change),
  };
};

const toStation = (apiStation: ApiStation): Station => ({
  name: apiStation.name,
  city: apiStation.region,
  latitude: apiStation.latitude,
  longitude: apiStation.longitude,
  pm25: getReading(apiStation, 'PM2.5'),
  pm10: getReading(apiStation, 'PM10'),
  co: getReading(apiStation, 'CO'),
  no2: getReading(apiStation, 'NO2'),
  o3: getReading(apiStation, 'O3'),
  so2: getReading(apiStation, 'SO2'),
});

export const fetchStations = (): Promise<Station[]> =>
  fetchWithCache('stations', async () => {
    const response = await fetch(STATIONS_URL);

    if (!response.ok) {
      throw new Error(`Failed to fetch stations: ${response.status}`);
    }

    const data = (await response.json()) as ApiStationsResponse;

    return Object.values(data).map(toStation);
  });
